'use client';

import { motion } from 'framer-motion';
import { SuccessStoryCard } from '@/sanity/pages/PageSuccessStoriesOverviewTemplate';
import ArticleCard from '@/components/pageComponents/successStoriesOverview/ArticleCard';
import { containerVariants, itemVariants } from '@/hooks/useEntranceAnimation';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';
import { useCarouselApi } from '@/hooks/useCarouselApi';

const LABEL: Record<string, string> = {
  en: 'You might also like',
  de: 'Das könnte dich auch interessieren',
  br: 'Você também pode gostar',
};

interface RelatedArticlesProps {
  articles: SuccessStoryCard[];
  language?: string;
}

export default function RelatedArticles({
  articles,
  language = 'en',
}: RelatedArticlesProps) {
  const {
    api,
    setApi,
    current,
    count,
    handleNext,
    handlePrev,
    isFirst,
    isLast,
  } = useCarouselApi();

  if (!articles?.length) return null;

  const limited = articles.slice(0, 10);

  const progress = count > 1 ? (current - 1) / (count - 1) : 1;

  return (
    <section className="w-full flex flex-col items-center pt-[7rem] pb-[10rem] lg:py-[12rem] border-t border-black/10">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="mx-auto w-[min(176.375rem,calc(100%-4rem))] lg:w-[min(176.375rem,calc(100%-8rem))] flex flex-col items-center gap-[3rem] lg:gap-[8rem]"
      >
        <motion.h2 variants={itemVariants}>
          {LABEL[language] ?? LABEL.en}
        </motion.h2>

        <div className="flex flex-col gap-[4rem] w-full">
          <Carousel
            setApi={setApi}
            opts={{ align: 'start' }}
            className="w-full -ml-[2rem]"
          >
            <CarouselContent>
              {limited.map((article) => (
                <CarouselItem
                  key={article._id}
                  className="basis-full lg:basis-1/3 pl-[2rem]"
                >
                  <motion.div variants={itemVariants} className="h-full">
                    <ArticleCard article={article} language={language} />
                  </motion.div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Navigation bar — mobile only */}
          <div className="flex lg:hidden items-center gap-[2.5rem]">
            <button
              onClick={handlePrev}
              disabled={isFirst}
              className="flex-shrink-0 w-[5rem] h-[5rem] rounded-[1rem] bg-[#c2ded6] flex items-center justify-center disabled:opacity-40 transition-opacity"
              aria-label="Previous slide"
            >
              <svg
                viewBox="0 0 7.125 12.5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-[0.875rem] h-[1.5rem] rotate-180"
              >
                <path
                  d="M7.125 6.25L0.875 12.5L0 11.625L5.375 6.25L0 0.875L0.875 0L7.125 6.25Z"
                  fill="#161616"
                />
              </svg>
            </button>

            <div className="flex-1 h-[0.25rem] bg-[rgba(109,169,145,0.2)]">
              <div
                className="h-full bg-[#228e71] transition-all duration-300"
                style={{ width: `${progress * 100}%` }}
              />
            </div>

            <button
              onClick={handleNext}
              disabled={isLast}
              className="flex-shrink-0 w-[5rem] h-[5rem] rounded-[1rem] bg-[#003828] flex items-center justify-center disabled:opacity-40 transition-opacity"
              aria-label="Next slide"
            >
              <svg
                viewBox="0 0 7.125 12.5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-[0.875rem] h-[1.5rem]"
              >
                <path
                  d="M7.125 6.25L0.875 12.5L0 11.625L5.375 6.25L0 0.875L0.875 0L7.125 6.25Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
